import { ReactNode, useEffect, useRef, useState } from "react";
import { XIcon } from "@phosphor-icons/react";
import { AppButton, ClickAwayListener } from "@libs/zapi-ui/custom";
import { Button } from "@libs/zapi-ui/ds/components/button";
import { useMediaQuery } from "@/zapi-frontend/shared/hooks/useMediaQuery";
import { cn } from "@/zapi-frontend/shared/lib/utils";
import { ChatFab } from "./ChatFab";
import { DockedChatCloseProvider } from "./DockedChatContext";
import { LeoLauncher } from "./LeoLauncher";
import { chatPanelWidthClasses } from "./ResponsiveChatPanel";

type TDockedChatIslandProps = {
  children: ReactNode;
  hasUnread?: boolean;
  hideFab?: boolean;
  onOpenChange?: (open: boolean) => void;
  testId?: string;
  // Whether the chat is docked in the layout. When omitted it docks at `hd` and wider, the
  // default for the create-project, new-version and presentation flows. The project pages pass
  // their own breakpoint so the memo column keeps its minimum width.
  docked?: boolean;
  // Positioning for the floating (overlay) state. Defaults to a full-height panel fixed to the
  // right edge of the viewport; the project pages pass an in-card `absolute` placement.
  floatingClassName?: string;
  // Selectors for controls that should NOT dismiss the open island when tapped (e.g. the
  // Project Setup / Case Frame tabs), so navigating between tabs keeps the chat open on mobile.
  keepOpenSelectors?: string[];
  // The language of the conversation inside, which can differ from the interface language. Set so
  // the browser draws Japanese text with Japanese glyph forms rather than Chinese ones.
  lang?: string;
  // `design-system` is the project page: docked, it is a closable column the FAB brings back; floating, its close
  // button is the design system's.
  appearance?: "default" | "design-system";
};

// Chat shell shared by the project pages and the create-project flow: docked when there is room,
// otherwise a FAB opening a slide-in island. Children stay mounted while closed so the chat keeps
// running in the background.
export function DockedChatIsland({
  children,
  hasUnread = false,
  hideFab = false,
  onOpenChange,
  testId,
  docked: dockedProp,
  floatingClassName,
  keepOpenSelectors,
  lang,
  appearance = "default"
}: TDockedChatIslandProps) {
  const dockedByDefault = useMediaQuery("(min-width: 1332px)");
  const docked = dockedProp ?? dockedByDefault;
  const [isOpen, setIsOpen] = useState(false);
  const [isDockClosed, setIsDockClosed] = useState(false);
  const dockRef = useRef<HTMLDivElement>(null);
  const focusComposerOnOpen = useRef(false);
  const isDock = docked && appearance === "design-system";
  const isDockVisible = isDock && !isDockClosed;

  // An open dock is in view, so whatever it shows has been seen; the launcher's dot only counts what
  // arrives while it is closed.
  useEffect(() => {
    if (isDockVisible) onOpenChange?.(true);
  }, [isDockVisible, onOpenChange]);

  useEffect(() => {
    dockRef.current?.toggleAttribute("inert", isDockClosed);
    if (!isDockClosed && focusComposerOnOpen.current) {
      focusComposerOnOpen.current = false;
      dockRef.current?.querySelector<HTMLElement>("textarea:not(:disabled)")?.focus();
    }
  }, [isDockClosed]);

  const handleOpenChange = (open: boolean) => {
    setIsOpen(open);
    onOpenChange?.(open);
  };

  if (isDock) {
    const closeDock = () => {
      setIsDockClosed(true);
      onOpenChange?.(false);
    };
    const reopenDock = () => {
      focusComposerOnOpen.current = true;
      setIsDockClosed(false);
    };
    return (
      <>
        {isDockClosed && !hideFab && <LeoLauncher hasUnread={hasUnread} onClick={reopenDock} focusOnMount />}
        <div
          ref={dockRef}
          data-testid={testId}
          data-docked-closed={isDockClosed || undefined}
          aria-hidden={isDockClosed || undefined}
          lang={lang}
          className={cn(
            "h-full shrink-0 overflow-hidden transition-[width,opacity] duration-300 ease-in-out",
            isDockClosed ? "w-0 opacity-0" : "w-[clamp(22rem,26vw,28rem)] opacity-100"
          )}
        >
          <div className="h-full w-[clamp(22rem,26vw,28rem)] overflow-hidden rounded-xl border bg-card">
            <DockedChatCloseProvider value={closeDock}>{children}</DockedChatCloseProvider>
          </div>
        </div>
      </>
    );
  }

  return (
    <>
      {!isOpen && !hideFab && !docked && appearance === "design-system" && (
        <LeoLauncher hasUnread={hasUnread} onClick={() => handleOpenChange(true)} />
      )}
      {!isOpen && !hideFab && !docked && appearance !== "design-system" && (
        <ChatFab
          hasUnread={hasUnread}
          onClick={() => handleOpenChange(true)}
          className={cn("z-20", floatingClassName && "size-[46px] bottom-5 right-5")}
        />
      )}
      <ClickAwayListener
        insideSelectors={keepOpenSelectors}
        onClickAway={() => {
          if (isOpen) handleOpenChange(false);
        }}
      >
        <div
          data-testid={testId}
          lang={lang}
          className={cn(
            "bg-white min-w-0 transition-all duration-300",
            docked
              ? cn("shrink-0 h-full border-none overflow-hidden", chatPanelWidthClasses)
              : cn(
                  "z-20 border-l **:data-chat-header:pr-12",
                  floatingClassName
                    ? cn(
                        "w-full mobile:max-w-[364px] border-innoveraZinc-100",
                        floatingClassName,
                        isOpen ? "translate-x-0" : "pointer-events-none translate-x-[calc(100%+0.5rem)]"
                      )
                    : cn(
                        "fixed top-0 h-svh tablet:h-full w-full mobile:max-w-[340px] overflow-hidden border-innoveraZinc-200",
                        isOpen ? "right-0" : "-right-full"
                      )
                )
          )}
        >
          {floatingClassName && !docked && isOpen && (
            <span className="pointer-events-none absolute inset-y-0 right-full w-[23px] bg-linear-to-l from-black/5 to-transparent" />
          )}
          {!docked && appearance === "design-system" && (
            <Button
              variant="ghost"
              size="icon"
              type="button"
              onClick={() => handleOpenChange(false)}
              aria-label="Close chat"
              className="absolute end-2 top-3 z-10"
            >
              <XIcon />
            </Button>
          )}
          {!docked && appearance !== "design-system" && (
            <AppButton
              appearance="text"
              color="zinc950"
              size="icon"
              onClick={() => handleOpenChange(false)}
              aria-label="Close chat"
              className="absolute top-3 right-2 z-10"
            >
              <XIcon />
            </AppButton>
          )}
          {children}
        </div>
      </ClickAwayListener>
    </>
  );
}
